// ============================================================
// Viashift — ViewAsBanner (Phase 3, Epic 3D.2)
// ============================================================
// Persistent amber banner rendered above Header in
// AuthenticatedLayout whenever SessionValue.viewAsRole is set.
// Communicates the demo mode unambiguously: shows the
// impersonated role, the real role, and an explicit caveat
// that server-side actions still execute under the real role.
//
// Tap X to exit. The picker that *enters* the impersonation
// lives in Header's dropdown menu (see Header.tsx).
//
// Why a separate banner rather than just a chrome tweak: the
// dropdown closes on selection and isn't normally visible
// during use. A persistent banner ensures the demoer can never
// forget they're impersonating — load-bearing for not showing
// the wrong audience the wrong view.
// ============================================================

import Icon from '@/components/Icon';
import { useSession, setViewAsRole, type ClinicRole } from '@/lib/session';

const ROLE_LABEL: Record<ClinicRole, string> = {
  owner: 'Owner',
  manager: 'Manager',
  staff: 'Staff',
};

export default function ViewAsBanner() {
  const { activeMembership, viewAsRole } = useSession();

  // Render nothing when not impersonating. Caller (App.tsx) also
  // gates on this, but a self-guard keeps the component safe to
  // drop in elsewhere without contract assumptions.
  if (viewAsRole === null) return null;

  const realRole = activeMembership.role;
  const onExit = (): void => setViewAsRole(null);

  return (
    <div
      role="status"
      aria-live="polite"
      className="bg-amber-50 border-b border-amber-200 px-3 py-2 flex items-center gap-2 text-amber-900"
    >
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center">
        <Icon name="user" className="w-4 h-4 text-amber-700" strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0 leading-tight">
        <div className="text-[12px] font-semibold">
          Demo mode: viewing as {ROLE_LABEL[viewAsRole]}
        </div>
        <div className="text-[10.5px] text-amber-800/90 mt-0.5">
          Real role: {ROLE_LABEL[realRole]} · actions still execute under your
          real role
        </div>
      </div>
      <button
        type="button"
        onClick={onExit}
        aria-label="Exit demo mode"
        className="flex-shrink-0 w-7 h-7 rounded-full hover:bg-amber-100 active:bg-amber-200 flex items-center justify-center transition"
      >
        <Icon name="x" className="w-4 h-4 text-amber-700" strokeWidth={2.5} />
      </button>
    </div>
  );
}
